import i18n from '@/utils/i18n';
import NoInternetIcon from '@assets/icons/no-internet.svg';
import React from 'react';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import { Button, Icons, Spinner, View } from '@usepepper/sprout-kit-ui';
import { canViewTeamProspects } from '@domain/access-control/policies/can-view-team-prospects';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { navigateToSalesPortalRoute } from '@/experiences/sales-portal/sales-portal-route-config';
import { ProspectsManagerViewContent } from '@/experiences/sales-portal/domain/prospects/screens/prospects/containers/prospects-manager-view-content';
import { ProspectsRepViewContent } from '@/experiences/sales-portal/domain/prospects/screens/prospects/containers/prospects-rep-view-content';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesHubShellTemplate } from '@/experiences/sales-portal/templates/sales-hub-shell-template';
import { SalesPortalStackParams } from '@/navigators/types';
import { StackScreenProps } from '@react-navigation/stack';
import { useSalesPortalAccess } from '@/experiences/sales-portal/blocs/hooks/use-sales-portal-access';
import { useTheme } from '@/contexts/theme-context';

type Props = StackScreenProps<
  SalesPortalStackParams,
  typeof SALES_PORTAL_ROUTES.Prospects
>;

export const ProspectsScreen: React.FC<Props> = ({ navigation, route }) => {
  const theme = useTheme();
  const { supplierUUID } = route.params;
  const { decide, error, loading, retry } = useSalesPortalAccess(supplierUUID);

  const isManagerView = !loading && !error && decide(canViewTeamProspects);

  const renderContent = () => {
    if (loading) {
      return (
        <View alignItems="center" height="100%" justifyContent="center">
          <Spinner />
        </View>
      );
    }
    if (error) {
      return (
        <ErrorState
          buttonLabel={i18n.t('sales_portal.error.refresh')}
          description={i18n.t('sales_portal.error.description')}
          icon={<NoInternetIcon stroke={theme.sk.color.icon.primary} />}
          onPress={retry}
          title={i18n.t('sales_portal.error.title')}
        />
      );
    }
    return isManagerView ? (
      <ProspectsManagerViewContent />
    ) : (
      <ProspectsRepViewContent />
    );
  };

  return (
    <SalesHubShellTemplate>
      <AppPageLayout
        actions={
          <Button
            icon={<Icons.MapPin color="$iconPrimary" size={16} />}
            onPress={() =>
              navigateToSalesPortalRoute(
                navigation,
                SALES_PORTAL_ROUTES.ProspectFinder,
                { supplierUUID },
              )
            }
            size="$sm"
            variant="secondary"
          >
            {i18n.t('sales_portal.prospects.find_new_prospects')}
          </Button>
        }
        bodyGutter={isManagerView ? '$0' : '$32'}
        mode={isManagerView ? 'fluid' : 'focused'}
        scroll={false}
        title={i18n.t('sales_portal.sidebar.prospects')}
      >
        {renderContent()}
      </AppPageLayout>
    </SalesHubShellTemplate>
  );
};
